/**
 * SupportChatProvider -- holds the help assistant's status, open state and the
 * conversation (D10-UI-003, #3604). Mounted once in the staff CRM tree, above
 * the routes, so a conversation survives every in-app navigation; "New chat"
 * clears it.
 *
 * The conversation also survives a page reload (#3624): it is kept in this
 * tab's sessionStorage under the signed-in user's id (`support-chat:v1:<id>`),
 * restored on load, removed by "New chat", removed for everyone on sign-out and
 * never shown to a different user. A reply that was still streaming when the
 * page went away comes back as finished (or as an error if it had no text), and
 * a restored reply's action never runs by itself -- only its chip remains.
 * Storage is best-effort: when it is unavailable the chat simply lives in
 * memory as before.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  getSupportStatus,
  streamSupportChat,
  SupportChatError,
  validateSupportAction,
  type SupportStatus,
} from "../api/support";
import { useAuth } from "./auth";
import {
  SupportChatContext,
  type ChatMessage,
  type SendOptions,
  type SupportActionHandler,
  type SupportChatValue,
} from "./supportChat";
import type { SupportAction } from "../api/support";
import {
  clearStoredChats,
  loadStoredChat,
  removeStoredChat,
  saveStoredChat,
} from "../lib/support/chatStorage";

let nextId = 0;
const newId = () => `m${Date.now().toString(36)}-${nextId++}`;

export function SupportChatProvider({ children }: { children: ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const userId = user?.id ?? null;

  const [status, setStatus] = useState<SupportStatus | null>(null);
  const statusRequested = useRef(false);
  // One round of status per signed-in user: an answer that lands after a
  // change of user (its yes/no about past chat use included, D10-UI-008) is
  // never applied, and the new round asks again for the new user.
  const statusUser = useRef<number | string | null>(userId);
  const [statusRound, setStatusRound] = useState(0);
  const statusRoundRef = useRef(statusRound);
  const [isOpen, setOpen] = useState(false);
  const [prefill, setPrefill] = useState<string | null>(null);
  const openWithPrefill = useCallback((text: string) => {
    setPrefill(text);
    setOpen(true);
  }, []);
  const clearPrefill = useCallback(() => setPrefill(null), []);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [conversationId, setConversationId] = useState<number | null>(null);
  const [awaitingFirstToken, setAwaitingFirstToken] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [offline, setOffline] = useState<SupportChatValue["offline"]>(null);
  const abortRef = useRef<AbortController | null>(null);
  // Bumped by every reset ("New chat", change of user): anything still
  // running for the conversation it replaced -- a reply's action waiting out
  // its reading pause, a refusal note -- sees it has gone stale and stops
  // (#3702).
  const epochRef = useRef(0);
  const actionHandlerRef = useRef<SupportActionHandler | null>(null);
  const isOpenRef = useRef(isOpen);
  const messagesRef = useRef<ChatMessage[]>(messages);
  const conversationIdRef = useRef<number | null>(conversationId);
  useEffect(() => {
    isOpenRef.current = isOpen;
    messagesRef.current = messages;
    conversationIdRef.current = conversationId;
  }, [isOpen, messages, conversationId]);

  const ensureStatus = useCallback(() => {
    if (statusRequested.current) return;
    statusRequested.current = true;
    const round = statusRound;
    getSupportStatus()
      .then((s) => {
        if (round !== statusRoundRef.current) return;
        setStatus(s);
        if (s.enabled && s.mode === "offline") setOffline({ reason: "status" });
      })
      // Unreachable status = treat as disabled: never draw a bubble that cannot help.
      .catch(() => {
        if (round === statusRoundRef.current) setStatus({ enabled: false });
      });
  }, [statusRound]);

  const reset = useCallback(() => {
    epochRef.current += 1;
    abortRef.current?.abort();
    abortRef.current = null;
    setMessages([]);
    setConversationId(null);
    setAwaitingFirstToken(false);
    setStreaming(false);
  }, []);

  // A different signed-in user (or sign-out) starts from scratch, status
  // included; the signed-in user gets back the conversation this tab held
  // before a reload (#3624). Sign-out clears every stored conversation.
  const restoredFor = useRef<number | string | null>(null);
  useEffect(() => {
    reset();
    setOpen(false);
    setOffline(null);
    setStatus(null);
    statusRequested.current = false;
    if (statusUser.current !== userId) {
      statusUser.current = userId;
      statusRoundRef.current += 1;
      setStatusRound(statusRoundRef.current);
    }
    restoredFor.current = null;
    if (userId === null) return;
    clearStoredChats(userId);
    const stored = loadStoredChat(userId);
    if (stored) {
      setMessages(stored.messages);
      setConversationId(stored.conversationId);
    }
    restoredFor.current = userId;
  }, [userId, reset]);

  // Signed out (not merely still loading): no conversation stays behind.
  useEffect(() => {
    if (userId === null && !authLoading) clearStoredChats();
  }, [userId, authLoading]);

  // Keep this tab's copy in step (best-effort; see loadStoredChat). "New chat"
  // and a change of user remove it explicitly.
  const clearedRef = useRef(false);
  useEffect(() => {
    if (userId === null || restoredFor.current !== userId) return;
    if (messages.length === 0) {
      // After "New chat" commits, remove again: a save queued by a reply that
      // was still streaming must not bring the old conversation back.
      if (clearedRef.current) removeStoredChat(userId);
      clearedRef.current = false;
      return;
    }
    saveStoredChat(userId, { conversationId, messages });
  }, [userId, conversationId, messages]);

  useEffect(() => () => abortRef.current?.abort(), []);

  const newChat = useCallback(() => {
    reset();
    setOffline((o) => (o?.reason === "status" ? o : null));
    if (userId !== null) removeStoredChat(userId);
    clearedRef.current = true;
  }, [reset, userId]);

  const retryChat = useCallback(() => setOffline(null), []);

  const setActionHandler = useCallback((handler: SupportActionHandler | null) => {
    actionHandlerRef.current = handler;
  }, []);

  /** Hand an action to the panel's handler; record a refusal note on its reply. */
  const dispatchAction = useCallback(
    async (messageId: string, action: SupportAction, convId: number | null) => {
      const handler = actionHandlerRef.current;
      if (!handler) return;
      const epoch = epochRef.current;
      const isStale = () => epochRef.current !== epoch;
      let note: string | void = undefined;
      try {
        note = await handler(action, { conversationId: convId, isStale });
      } catch {
        note = "Sorry, I couldn't do that just now.";
      }
      if (isStale()) return;
      setMessages((prev) =>
        prev.map((m) => (m.id === messageId ? { ...m, actionNote: note || undefined } : m)),
      );
    },
    [],
  );

  const runMessageAction = useCallback(
    (messageId: string) => {
      const msg = messagesRef.current.find((m) => m.id === messageId);
      if (msg?.action) void dispatchAction(messageId, msg.action, conversationIdRef.current);
    },
    [dispatchAction],
  );

  const send = useCallback(
    async (text: string, opts: SendOptions) => {
      const message = text.trim();
      if (!message || abortRef.current) return;
      const controller = new AbortController();
      abortRef.current = controller;

      const replyId = newId();
      setMessages((prev) => [
        ...prev,
        { id: newId(), role: "user", text: message, status: "done" },
        { id: replyId, role: "assistant", text: "", status: "streaming" },
      ]);
      setAwaitingFirstToken(true);
      setStreaming(true);

      const patchReply = (patch: Partial<ChatMessage> | ((m: ChatMessage) => Partial<ChatMessage>)) =>
        setMessages((prev) =>
          prev.map((m) =>
            m.id === replyId ? { ...m, ...(typeof patch === "function" ? patch(m) : patch) } : m,
          ),
        );
      const dropReply = () => setMessages((prev) => prev.filter((m) => m.id !== replyId));

      try {
        const events = streamSupportChat(
          {
            conversation_id: conversationId,
            message,
            route: opts.route,
            allowed_routes: opts.allowedRoutes,
            allowed_tours: opts.allowedTours,
          },
          controller.signal,
        );
        let finished = false;
        let completed = false;
        let errored = false;
        let pending: SupportAction | null = null;
        let streamConversationId = conversationId;
        for await (const event of events) {
          if (controller.signal.aborted) return;
          switch (event.type) {
            case "meta":
              if (event.conversationId) {
                setConversationId(event.conversationId);
                streamConversationId = event.conversationId;
              }
              break;
            case "token":
              setAwaitingFirstToken(false);
              patchReply((m) => ({ text: m.text + event.text }));
              break;
            case "replace":
              // The server withdrew words claiming an action it did not run, or
              // linked a page the answer only named (D10-UI-010).
              setAwaitingFirstToken(false);
              patchReply({ text: event.text });
              break;
            case "action": {
              // D10-SEC-002: re-validate before anything could run it.
              const action = validateSupportAction(event.raw, {
                allowedTours: opts.allowedTours,
                allowedRoutes: opts.allowedRoutes,
                currentRoute: opts.route,
              });
              if (action) {
                // Runs after `done`: the reply is stored by then (so the
                // feedback excerpt includes it) and the user has read it.
                patchReply({ action });
                pending = action;
              }
              break;
            }
            case "error":
              errored = true;
              patchReply((m) => ({
                status: "error",
                text: m.text ? `${m.text}\n\n${event.message}` : event.message,
              }));
              finished = true;
              break;
            case "done":
              patchReply((m) => ({
                status: m.status === "error" ? "error" : "done",
                truncated: event.truncated,
              }));
              finished = true;
              completed = true;
              break;
            case "offline":
              if (event.conversationId) setConversationId(event.conversationId);
              dropReply();
              setOffline({ reason: event.reason });
              finished = true;
              break;
          }
        }
        // D10-ACT: carry out the reply's one action once it has completed --
        // never after an error, and not if the user closed the panel meanwhile
        // (the reply keeps a chip to run it later).
        if (completed && !errored && pending && isOpenRef.current && !controller.signal.aborted) {
          void dispatchAction(replyId, pending, streamConversationId);
        }
        if (!finished && !controller.signal.aborted) {
          patchReply((m) => ({
            status: m.text ? "done" : "error",
            text: m.text || "Sorry, something went wrong. Please try again.",
          }));
        }
      } catch (err) {
        if (controller.signal.aborted) return;
        const msg =
          err instanceof SupportChatError ? err.message : "Sorry, something went wrong. Please try again.";
        patchReply((m) => ({ status: "error", text: m.text ? `${m.text}\n\n${msg}` : msg }));
      } finally {
        if (abortRef.current === controller) {
          abortRef.current = null;
          setAwaitingFirstToken(false);
          setStreaming(false);
        }
      }
    },
    [conversationId, dispatchAction],
  );

  const value = useMemo<SupportChatValue>(
    () => ({
      status,
      ensureStatus,
      isOpen,
      setOpen,
      prefill,
      openWithPrefill,
      clearPrefill,
      messages,
      conversationId,
      awaitingFirstToken,
      streaming,
      offline,
      retryChat,
      send,
      newChat,
      setActionHandler,
      runMessageAction,
    }),
    [
      status,
      ensureStatus,
      isOpen,
      prefill,
      openWithPrefill,
      clearPrefill,
      messages,
      conversationId,
      awaitingFirstToken,
      streaming,
      offline,
      retryChat,
      send,
      newChat,
      setActionHandler,
      runMessageAction,
    ],
  );

  return <SupportChatContext.Provider value={value}>{children}</SupportChatContext.Provider>;
}
